/**
 * Uploads and media-library operations, through the backend (PM 65d0edd2).
 *
 * The web app has no Supabase session; it runs as `anon`. Storage no longer
 * lets anon write anywhere, so the browser never calls supabase.storage to
 * upload, list, move or remove. It asks the backend, which checks the caller,
 * the permission and the path:
 *   - uploads: the backend returns a one-time SIGNED UPLOAD URL and the file
 *     is PUT straight to storage with it (a photo never streams through the
 *     backend's 6 MB request limit). The token is good for that one path only.
 *   - list / folders / move / remove: the backend does them itself.
 *
 * Every function throws an Error carrying the backend's message on failure;
 * callers show it.
 */

import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

export interface SignedUpload {
  path: string;
  token: string;
  signedUrl: string;
  bucket: string;
  publicUrl?: string | null;
}

export interface MediaFolderEntry {
  id: string | null;
  name: string;
  created_at?: string;
  updated_at?: string;
  metadata?: { size?: number; mimetype?: string; [key: string]: unknown };
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: unknown; message?: unknown } }; message?: string };
  const fromBody = e?.response?.data?.error ?? e?.response?.data?.message;
  if (typeof fromBody === "string" && fromBody) return fromBody;
  return fallback;
}

async function postForData<T>(url: string, body: unknown, fallback: string): Promise<T> {
  try {
    const res = await axios.post(url, body);
    return res.data?.data as T;
  } catch (err) {
    throw new Error(errorMessage(err, fallback));
  }
}

/** PUT the bytes to a signed upload URL. No Supabase client or key needed. */
export async function putToSignedUrl(signedUrl: string, file: Blob, contentType: string): Promise<void> {
  const res = await fetch(signedUrl, {
    method: "PUT",
    headers: { "content-type": contentType, "x-upsert": "false" },
    body: file,
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.message || body?.error || "";
    } catch {
      // not JSON; the status says enough
    }
    throw new Error(detail ? `Upload failed: ${detail}` : `Upload failed (${res.status})`);
  }
}

interface UploadDescriptor {
  fileName: string;
  contentType: string;
  size: number;
}

// Some browsers report an empty type for Office files (and HEIC on Windows).
const TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  svg: "image/svg+xml",
};

function describe(file: Blob, fileName?: string, contentType?: string): UploadDescriptor {
  const name = fileName || (file as File).name || "file";
  const ext = name.includes(".") ? (name.split(".").pop() || "").toLowerCase() : "";
  return {
    fileName: name,
    contentType: contentType || file.type || TYPE_BY_EXTENSION[ext] || "application/octet-stream",
    size: file.size,
  };
}

async function signAndPut(url: string, extra: Record<string, unknown>, file: Blob, d: UploadDescriptor): Promise<SignedUpload> {
  const signed = await postForData<SignedUpload>(url, { ...extra, ...d }, "Could not start the upload.");
  if (!signed?.signedUrl) throw new Error("Could not start the upload.");
  await putToSignedUrl(signed.signedUrl, file, d.contentType);
  return signed;
}

// ---------------------------------------------------------------------------
// Media library (`media` bucket)
// ---------------------------------------------------------------------------

/** Upload one file into a media-library folder. Resolves the stored path and its public URL. */
export async function uploadMediaFile(
  folder: string,
  file: Blob,
  opts: { fileName?: string; contentType?: string } = {}
): Promise<{ path: string; publicUrl: string }> {
  const d = describe(file, opts.fileName, opts.contentType);
  const signed = await signAndPut(`${BASE_URL}/media-library/upload-url`, { folder }, file, d);
  return { path: signed.path, publicUrl: signed.publicUrl || "" };
}

export async function listMediaFolder(
  path: string,
  opts: { sortBy?: "name" | "created_at"; order?: "asc" | "desc" } = {}
): Promise<MediaFolderEntry[]> {
  try {
    const res = await axios.get(`${BASE_URL}/media-library/list`, {
      params: { path, sortBy: opts.sortBy || "name", order: opts.order || "asc" },
    });
    return (res.data?.data as MediaFolderEntry[]) || [];
  } catch (err) {
    throw new Error(errorMessage(err, "Failed to load files."));
  }
}

export async function createMediaFolder(path: string): Promise<void> {
  await postForData(`${BASE_URL}/media-library/folders`, { path }, "Failed to create folder.");
}

export async function removeMediaFiles(paths: string[]): Promise<void> {
  await postForData(`${BASE_URL}/media-library/remove`, { paths }, "Delete failed.");
}

export async function moveMediaFile(from: string, to: string): Promise<void> {
  await postForData(`${BASE_URL}/media-library/move`, { from, to }, "Move failed.");
}

// ---------------------------------------------------------------------------
// Feature uploads
// ---------------------------------------------------------------------------

/** A facility resource photo (`facility-images`, public). Resolves its public URL. */
export async function uploadFacilityImage(file: File): Promise<string> {
  const signed = await signAndPut(`${BASE_URL}/facility/images/upload-url`, {}, file, describe(file));
  return signed.publicUrl || "";
}

/** An event photo (`event-attachments`, public). Record it with uploadEventAttachment afterwards. */
export async function uploadEventPhoto(eventId: string, file: File): Promise<{ path: string; publicUrl: string }> {
  const signed = await signAndPut(
    `${BASE_URL}/events/${encodeURIComponent(eventId)}/attachments/upload-url`,
    {},
    file,
    describe(file)
  );
  return { path: signed.path, publicUrl: signed.publicUrl || "" };
}

/** An incident attachment (`incident-attachments`, private). Record it with recordIncidentAttachment afterwards. */
export async function uploadIncidentAttachmentFile(incidentId: string, file: File): Promise<{ path: string }> {
  const signed = await signAndPut(
    `${BASE_URL}/incident-reports/${encodeURIComponent(incidentId)}/attachments/upload-url`,
    {},
    file,
    describe(file)
  );
  return { path: signed.path };
}
